import React from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus } from "lucide-react";

import ModularWorkspace from "./components/ModularWorkspace";
import BackpackDrawer from "./components/BackpackDrawer";
import SaveSnippetDialog from "./components/SaveSnippetDialog";
import SaveSpriteDialog from "./components/SaveSpriteDialog";
import WorkspaceNavbar from "./components/WorkspaceNavbar";
import WorkspaceDialog from "./components/WorkspaceDialog";
import AddChildModal from "../dashboard/parent/components/AddChildModal";

import { useWorkspaceDialog } from "./hooks/useWorkspaceDialog";
import { useWorkspaceStudent } from "./hooks/useWorkspaceStudent";
import { useWorkspaceProject } from "./hooks/useWorkspaceProject";
import { useLocalDraftAutoSave } from "./hooks/useLocalDraftAutoSave";
import { useWorkspaceFileIO } from "./hooks/useWorkspaceFileIO";
import { useWorkspaceBackpackBridge } from "./hooks/useWorkspaceBackpackBridge";

/*
 * Main Code Workspace Page.
 * Acts as the declarative UI orchestrator, delegating state and side effects
 * to domain-specific custom hooks.
 */
const CodeWorkspacePage = () => {
  const navigate = useNavigate();

  // 0. Ensure Cross-Origin Isolation for Pyodide & SharedArrayBuffer interactive input()
  React.useEffect(() => {
    if (!window.crossOriginIsolated) {
      const reloadKey = "coi_workspace_reloaded";
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "true");
        window.location.reload();
      }
    } else {
      sessionStorage.removeItem("coi_workspace_reloaded");
    }
  }, []);

  // 1. Student Context & Studio Environment
  const student = useWorkspaceStudent();

  // 2. Centralized Dialog Controller
  const dialog = useWorkspaceDialog();

  // 3. Cloud Project Persistence & Lifecycle
  const project = useWorkspaceProject({
    selectedChild: student.selectedChild,
    activeWorkspaceEnv: student.activeWorkspaceEnv,
    setActiveWorkspaceEnv: student.setActiveWorkspaceEnv,
    openDialog: dialog.openDialog,
    onClearDraft: () => draft.clearDraft(),
  });

  // 4. Local Storage Autosave & Crash Recovery
  const draft = useLocalDraftAutoSave({
    selectedChild: student.selectedChild,
    activeWorkspaceEnv: student.activeWorkspaceEnv,
    activeWorkspaceCode: project.activeWorkspaceCode,
    activeWorkspaceFiles: project.activeWorkspaceFiles,
    projectName: project.projectName,
    projectId: project.projectId,
    lastSavedCloudCode: project.lastSavedCloudCode,
    baselineCode: project.baselineCode,
    setProjectName: project.setProjectName,
    setProjectId: project.setProjectId,
    setLoadedProjectData: project.setLoadedProjectData,
    setActiveWorkspaceCode: project.setActiveWorkspaceCode,
    setActiveWorkspaceFiles: project.setActiveWorkspaceFiles,
    setLastSavedCloudCode: project.setLastSavedCloudCode,
    setBaselineCode: project.setBaselineCode,
    openDialog: dialog.openDialog,
  });

  // 5. Local File Uploads (.py, .js, .sb3) & Downloads
  const fileIO = useWorkspaceFileIO({
    activeWorkspaceEnv: student.activeWorkspaceEnv,
    setActiveWorkspaceEnv: student.setActiveWorkspaceEnv,
    activeWorkspaceCode: project.activeWorkspaceCode,
    activeWorkspaceFiles: project.activeWorkspaceFiles,
    projectName: project.projectName,
    setProjectName: project.setProjectName,
    setProjectId: project.setProjectId,
    setLoadedProjectData: project.setLoadedProjectData,
    setActiveWorkspaceCode: project.setActiveWorkspaceCode,
    setActiveWorkspaceFiles: project.setActiveWorkspaceFiles,
    setLastSavedCloudCode: project.setLastSavedCloudCode,
    setBaselineCode: project.setBaselineCode,
    setWorkspaceKey: project.setWorkspaceKey,
    isDirty: project.isDirty,
    openDialog: dialog.openDialog,
    onClearDraft: () => draft.clearDraft(),
  });

  // 6. Backpack Drawer & Sprite/Snippet Cloud Bridge
  const backpack = useWorkspaceBackpackBridge({
    selectedChild: student.selectedChild,
    activeWorkspaceEnv: student.activeWorkspaceEnv,
    parentsDetails: student.parentsDetails,
  });

  // Dialog Confirm / Cancel Router
  const handleDialogConfirm = () => {
    const { type, payload, inputValue } = dialog.dialogConfig;

    if (type === "RESTORE_DRAFT") {
      draft.restoreDraftData(payload);
    } else if (type === "NEW_PROJECT") {
      project.executeNewProject();
    } else if (type === "SWITCH_LANGUAGE") {
      project.executeLanguageSwitch(payload);
    } else if (type === "LOAD_PROJECT") {
      project.executeLoadProjectFromCloud(payload);
    } else if (type === "LOAD_LOCAL_PROJECT") {
      fileIO.executeLoadLocalProject(payload);
    } else if (type === "RENAME") {
      const val = inputValue.trim() || "Unnamed Project";
      if (payload === "save") project.executeSaveToCloud(val);
      if (payload === "download") fileIO.executeDownloadLocal(val);
    }
    dialog.closeDialog();
  };

  const handleDialogCancel = () => {
    if (dialog.dialogConfig.type === "RESTORE_DRAFT") {
      draft.clearDraft();
      project.executeNewProject();
    }
    dialog.closeDialog();
  };

  const isScratchTheme =
    student.activeWorkspaceEnv === "scratch" ||
    student.activeWorkspaceEnv === "junior";

  return (
    <div className="w-full h-[calc(100vh-75px)] bg-[#001E3B] flex flex-col overflow-hidden relative">
      {/* Hidden file input for uploading */}
      <input
        type="file"
        ref={fileIO.fileInputRef}
        style={{ display: "none" }}
        onChange={fileIO.handleFileChange}
        accept=".zip,.py,.js,.sb3"
      />

      {/* Compact Workspace Navigation Bar */}
      <WorkspaceNavbar
        isScratchTheme={isScratchTheme}
        onBack={() => navigate("/dashboard")}
        selectedChild={student.selectedChild}
        isStudentAccount={student.isStudentAccount}
        projectName={project.projectName}
        onProjectNameChange={project.setProjectName}
        onNewProject={project.handleNewProjectClick}
        onSaveProject={project.handleSaveClick}
        isSaving={project.isSaving}
        isDirty={project.isDirty}
        onDownloadProject={fileIO.handleDownloadClick}
        onOpenBackpack={() => backpack.setShowBackpackDrawer(true)}
      />

      {/* Main Dynamic Modular Workspace Factory or Empty State Prompt */}
      <div className="flex-1 w-full bg-slate-900 overflow-hidden relative flex flex-col">
        {!student.selectedChild && student.childrenList.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-white bg-slate-900">
            <div className="w-16 h-16 rounded-3xl bg-purple-600/20 border border-purple-500/40 text-purple-400 flex items-center justify-center mb-4 shadow-xl">
              <UserPlus className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-black text-white mb-2">
              No Student Added Yet
            </h2>
            <p className="text-sm text-slate-300 max-w-md mb-6 leading-relaxed">
              Please add your child's details to initialize Practido Code Studio
              tailored specifically to their class level!
            </p>
            <button
              onClick={() => student.setShowAddChildModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-2xl font-extrabold text-sm shadow-lg shadow-purple-500/20 flex items-center gap-2 transition cursor-pointer"
            >
              <UserPlus className="w-4 h-4" />
              <span>+ Add Student / Child</span>
            </button>
          </div>
        ) : (
          <ModularWorkspace
            key={project.workspaceKey} // Forces complete remount on New Project / File Load
            mode="free_play"
            environment={student.activeWorkspaceEnv}
            selectedChild={student.selectedChild}
            onCodeChange={project.handleWorkspaceCodeChange}
            initialData={project.loadedProjectData}
            onRequestLanguageChange={project.handleRequestLanguageChange}
            onSaveSnippet={backpack.handleTriggerSaveSnippet}
            onSaveSprite={backpack.handleTriggerSaveSprite}
          />
        )}
      </div>

      {/* Drawers & Modals */}
      <BackpackDrawer
        isOpen={backpack.showBackpackDrawer}
        onClose={() => backpack.setShowBackpackDrawer(false)}
        childId={student.selectedChild?.id || student.selectedChild?.uid}
        environment={backpack.activeContextEnv || student.activeWorkspaceEnv}
        theme={isScratchTheme ? "light" : "dark"}
        onLoadProject={project.handleLoadProjectFromCloud}
        onUploadLocal={fileIO.handleUploadLocalClick}
        onInsertSnippet={backpack.handleInsertSnippet}
        onInsertSprite={backpack.handleInsertSprite}
      />

      <SaveSnippetDialog
        isOpen={backpack.showSaveSnippetDialog}
        onClose={() => backpack.setShowSaveSnippetDialog(false)}
        onSave={backpack.handleSaveSnippetToCloud}
        initialName={
          backpack.activeSnippetToSave?.defaultName || "Unnamed Snippet"
        }
        snippetData={backpack.activeSnippetToSave}
        theme={isScratchTheme ? "light" : "dark"}
      />

      <SaveSpriteDialog
        isOpen={backpack.showSaveSpriteDialog}
        onClose={() => backpack.setShowSaveSpriteDialog(false)}
        onSave={backpack.handleSaveSpriteToCloud}
        initialName={
          backpack.activeSpriteToSave?.defaultName || "Unnamed Sprite"
        }
        spriteData={backpack.activeSpriteToSave}
        theme={isScratchTheme ? "light" : "dark"}
      />

      <AddChildModal
        isOpen={student.showAddChildModal}
        onClose={() => student.setShowAddChildModal(false)}
        onChildAdded={student.handleChildAdded}
        parentsDetails={student.parentsDetails}
      />

      {/* Custom Global Dialog Component */}
      <WorkspaceDialog
        dialogConfig={dialog.dialogConfig}
        onClose={dialog.closeDialog}
        onCancel={handleDialogCancel}
        onConfirm={handleDialogConfirm}
        onInputChange={dialog.setInputValue}
        activeWorkspaceEnv={student.activeWorkspaceEnv}
      />
    </div>
  );
};

export default CodeWorkspacePage;
